import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { formatBettingLine } from '../../utils/gameDetailUtils';
import styles from './GameDetailModal.module.css';

const GameDetailPickGrid = ({
  game,
  selectedPick,
  onSelectPick,
  hasReachedMaxPicks,
  isPickLockedByOther,
  isPickWatched,
  isGameLockable,
  confirmActions = null,
}) => {
  const hasSpread = game.betting?.spread != null;
  const hasOverUnder = game.betting?.over_under != null;

  const handlePickClick = useCallback((pickType, lineValue, isSelected, isDisabled) => {
    if (isDisabled) return;
    if (isSelected) {
      onSelectPick(null, null);
    } else {
      onSelectPick(pickType, lineValue);
    }
  }, [onSelectPick]);

  const renderPickButton = useCallback((pickType, label, lineValue, disabled) => {
    const isSelected = selectedPick?.pickType === pickType;
    const isLocked = isPickLockedByOther(pickType);
    const isWatched = isPickWatched(pickType);
    const isDisabled = disabled || hasReachedMaxPicks || isLocked;

    return (
      <button
        type="button"
        className={`${styles.pickButton} ${isSelected ? styles.selected : ''} ${!isSelected && isWatched && !isLocked ? styles.watched : ''} ${isDisabled ? styles.disabled : ''}`}
        onClick={() => handlePickClick(pickType, lineValue, isSelected, isDisabled)}
        disabled={isDisabled}
        aria-pressed={isSelected}
      >
        <span className={styles.pickButtonLabel}>{label}</span>
        <span className={styles.pickButtonLine}>{formatBettingLine(lineValue)}</span>
        {isLocked && <span className={styles.lockedIndicator}>LOCKED</span>}
      </button>
    );
  }, [selectedPick, hasReachedMaxPicks, isPickLockedByOther, isPickWatched, handlePickClick]);

  if (!isGameLockable) {
    return null;
  }

  return (
    <div className={styles.section}>
      <h3 className={styles.sectionTitle}>Pick Options</h3>
      {hasReachedMaxPicks && (
        <div className={styles.maxPicksWarning}>
          You have reached the maximum number of picks for this week.
        </div>
      )}
      <div className={styles.pickGrid}>
        {renderPickButton(
          'away',
          <>
            {game.away_team.rank && <span className={styles.rankBadge}>#{game.away_team.rank}</span>}
            {game.away_team.abbreviation || game.away_team.name}
          </>,
          hasSpread ? -game.betting.spread : null,
          !hasSpread
        )}
        {renderPickButton(
          'home',
          <>
            {game.home_team.abbreviation || game.home_team.name}
            {game.home_team.rank && <span className={styles.rankBadge}>#{game.home_team.rank}</span>}
          </>,
          hasSpread ? game.betting.spread : null,
          !hasSpread
        )}
        {renderPickButton('over', 'Over', hasOverUnder ? game.betting.over_under : null, !hasOverUnder)}
        {renderPickButton('under', 'Under', hasOverUnder ? game.betting.over_under : null, !hasOverUnder)}
      </div>
      {confirmActions}
    </div>
  );
};

GameDetailPickGrid.propTypes = {
  game: PropTypes.object.isRequired,
  selectedPick: PropTypes.shape({
    pickType: PropTypes.string.isRequired,
    lockLine: PropTypes.number.isRequired,
  }),
  onSelectPick: PropTypes.func.isRequired,
  hasReachedMaxPicks: PropTypes.bool.isRequired,
  isPickLockedByOther: PropTypes.func.isRequired,
  isPickWatched: PropTypes.func.isRequired,
  isGameLockable: PropTypes.bool.isRequired,
  confirmActions: PropTypes.node,
};

export default GameDetailPickGrid;
